// Shared utilities and constants for the PDF report builder.
// Kept tiny on purpose so any new report can import what it needs.

// ---------- Constants ----------
export const NAVY = [0, 31, 92];

export const COLORS = [
  [59, 130, 246],   // blue
  [34, 197, 94],    // green
  [249, 115, 22],   // orange
  [168, 85, 247],   // purple
  [236, 72, 153],   // pink
  [20, 184, 166],   // teal
  [245, 158, 11],   // amber
  [99, 102, 241],   // indigo
];

export const PAGE_LABELS = {
  'dashboard': 'Dashboard Overview',
  'spend-analysis': 'Spend Analysis',
  'spend-distribution': 'Spend Distribution',
  'category-console': 'Category Console',
  'commonality': 'Commonality Analysis',
  'comparative': 'Comparative Analysis',
  'p2p-analytics': 'P2P Analytics',
  'industry-benchmark': 'Industry Benchmark',
  'supplier-directory': 'Supplier Directory',
  'suppliers': 'Supplier Management',
  'forecasting': 'Spend Forecasting',
  'data-quality': 'Data Quality',
  'governance': 'Governance',
};

const CURRENCY_SYMBOLS = {
  GBP: '£', USD: '$', EUR: '€', PKR: 'Rs', INR: '₹',
  AUD: 'A$', CAD: 'C$', JPY: '¥', CHF: 'CHF ',
};

// ---------- Text & currency helpers ----------

// Sanitize characters jsPDF's built-in fonts can't render. When a Unicode
// font (Noto Sans / SimSun) is active, non-Latin-1 characters are kept.
let _unicodeFontsActive = false;
export function setUnicodeFontsActive(active) { _unicodeFontsActive = !!active; }

export function safeText(text) {
  if (!text) return '';
  const s = String(text)
    .replace(/[\u201C\u201D\u201E\u201F\u2033]/g, '"')
    .replace(/[\u2018\u2019\u201A\u201B\u2032]/g, "'")
    .replace(/[\u2013\u2014\u2015]/g, '-')
    .replace(/\u2026/g, '...')
    .replace(/[\u00A0]/g, ' ');
  return _unicodeFontsActive ? s : s.replace(/[^\x00-\xFF]/g, '');
}

export function getCurrencySymbol() {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const code = user?.default_currency || 'GBP';
    return CURRENCY_SYMBOLS[code] || code + ' ';
  } catch { return '£'; }
}

export function formatValue(val) {
  const cs = getCurrencySymbol();
  if (val >= 1000000000) return `${cs}${(val / 1000000000).toFixed(1)}B`;
  if (val >= 1000000) return `${cs}${(val / 1000000).toFixed(1)}M`;
  if (val >= 1000) return `${cs}${(val / 1000).toFixed(0)}K`;
  return `${cs}${Math.round(val).toLocaleString()}`;
}

// ---------- Filter helpers ----------

export function getSavedFilters(pageName) {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const orgScope = user?.organization_id || null;
    // SECURITY: only read the org-scoped key. If the user isn't logged in or the
    // key doesn't exist, return empty — never fall back to a legacy unscoped key
    // as that can bleed a prior session's filter (e.g., another org's supplier)
    // into a different user's report.
    if (!orgScope) return {};
    const raw = localStorage.getItem(`pca_filters_${orgScope}_${pageName}`);
    if (!raw) return {};
    const f = JSON.parse(raw);
    const active = {};
    for (const [k, v] of Object.entries(f)) {
      if (v && v !== 'all') active[k] = v;
    }
    return active;
  } catch { return {}; }
}

export function filtersToParams(filters) {
  const params = new URLSearchParams();
  if (filters.category) params.append('category', filters.category);
  if (filters.category_l2) params.append('category_l2', filters.category_l2);
  if (filters.category_l3) params.append('category_l3', filters.category_l3);
  if (filters.supplier) params.append('supplier', filters.supplier);
  if (filters.region) params.append('region', filters.region);
  if (filters.country) params.append('country', filters.country);
  if (filters.sbu) params.append('sbu', filters.sbu);
  if (filters.fiscal_year) params.append('fiscal_year', filters.fiscal_year);
  if (filters.startDate) params.append('start_date', filters.startDate);
  if (filters.endDate) params.append('end_date', filters.endDate);
  return params.toString();
}

export function filtersDescription(filters) {
  const parts = [];
  if (filters.category) parts.push(`Category: ${filters.category}`);
  if (filters.category_l2) parts.push(`Category L2: ${filters.category_l2}`);
  if (filters.category_l3) parts.push(`Category L3: ${filters.category_l3}`);
  if (filters.supplier) parts.push(`Supplier: ${filters.supplier}`);
  if (filters.region) parts.push(`Region: ${filters.region}`);
  if (filters.country) parts.push(`Country: ${filters.country}`);
  if (filters.sbu) parts.push(`SBU: ${filters.sbu}`);
  if (filters.fiscal_year) parts.push(`FY: ${filters.fiscal_year}`);
  if (filters.startDate) parts.push(`From: ${new Date(filters.startDate).toLocaleDateString()}`);
  if (filters.endDate) parts.push(`To: ${new Date(filters.endDate).toLocaleDateString()}`);
  return parts.length > 0 ? parts.join(' | ') : 'All Data (No Filters)';
}

// ---------- Common renderers ----------

export function renderFilterBanner(doc, y, filterDesc) {
  if (filterDesc === 'All Data (No Filters)') return y;
  doc.setFillColor(240, 238, 255);
  doc.roundedRect(14, y - 3, doc.internal.pageSize.getWidth() - 28, 8, 2, 2, 'F');
  doc.setFontSize(7);
  doc.setFont('Helvetica', 'italic');
  doc.setTextColor(90, 60, 150);
  doc.text(safeText(`Active Filters: ${filterDesc}`), 18, y + 2);
  doc.setTextColor(0);
  return y + 10;
}

export function renderTitlePage(doc, reportName, sectionsCount, orgName = '') {
  const pw = doc.internal.pageSize.getWidth();
  doc.setFillColor(...NAVY);
  doc.rect(0, 0, pw, 45, 'F');
  doc.setFont('Helvetica', 'bold');
  doc.setTextColor(255, 255, 255);

  // Auto-fit title: shrink font so it fits on one line, wrap to 2 lines max.
  let titleFont = 22;
  const maxTitleWidth = pw - 28;
  doc.setFontSize(titleFont);
  while (titleFont > 14 && doc.getTextWidth(reportName) > maxTitleWidth) {
    titleFont -= 1;
    doc.setFontSize(titleFont);
  }
  const titleLines = doc.splitTextToSize(reportName, maxTitleWidth);
  const maxLines = Math.min(titleLines.length, 2);
  if (maxLines > 1) {
    doc.setFillColor(...NAVY);
    doc.rect(0, 0, pw, 55, 'F');
  }
  for (let i = 0; i < maxLines; i++) {
    doc.text(titleLines[i], 14, 22 + i * (titleFont * 0.45));
  }
  const bannerBottom = maxLines > 1 ? 55 : 45;
  const metaY = maxLines > 1 ? 40 : 32;
  doc.setFontSize(10);
  doc.setFont('Helvetica', 'normal');
  doc.setTextColor(255, 255, 255);
  doc.text(`Generated: ${new Date().toLocaleString()}`, 14, metaY);
  doc.setFontSize(8);
  doc.text(safeText(`Sections: ${sectionsCount} | ${orgName ? `${orgName} • ` : ''}SpendLens`), 14, metaY + 7);

  return bannerBottom + 10;
}

export function renderSectionHeader(doc, y, label) {
  const pw = doc.internal.pageSize.getWidth();
  doc.setFillColor(...NAVY);
  doc.rect(0, y - 5, pw, 10, 'F');
  doc.setFontSize(12);
  doc.setFont('Helvetica', 'bold');
  doc.setTextColor(255, 255, 255);
  doc.text(label, 14, y + 2);
  return y + 12;
}


// ---------- AI fetch helper ----------
// Maps the report's page key to the page key used by the live UI AIPanel,
// so the server-side recommendations cache (keyed by `recs:<page>`) is shared.
const REPORT_PAGE_TO_RECS_PAGE = {
  'p2p-analytics': 'p2p',
  'industry-benchmark': 'benchmark',
};

/**
 * Fetch insights + recommendations for one report page in parallel.
 * - Insights come from the deeper `/ai/report-insights` (4-insight prompt).
 * - Recommendations come from `/ai/recommendations` (same 5-rec CPO output as the
 *   live UI, and shares the same server cache → instant on a warm cache).
 * Either call can fail independently without blocking the other.
 */
export async function fetchPageAI(api, { page, dataContext, filterDesc, filters }) {
  const recsPage = REPORT_PAGE_TO_RECS_PAGE[page] || page;

  const insightsP = api
    .post('/ai/report-insights', { page, data_context: dataContext, filters_description: filterDesc })
    .then(r => r.data?.insights || [])
    .catch(() => []);

  const recsP = api
    .post('/ai/recommendations', { page: recsPage, filters: filters || {} })
    .then(r => r.data?.recommendations || [])
    .catch(() => []);

  const [insights, recommendations] = await Promise.all([insightsP, recsP]);
  return { insights, recommendations };
}
